import { cue } from './feedback';
import { useEffect, useRef, useState } from 'react';

/**
 * Whether an element brings up the on-screen keyboard when focused.
 *
 * A slider or a checkbox takes focus too, but no keyboard comes with it, so they
 * must not count — otherwise tapping the rounds slider would look like typing.
 */
export function isTextField(el: Element | EventTarget | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el as HTMLInputElement).type;
  return !['range', 'checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'color', 'hidden'].includes(
    type,
  );
}

/**
 * Tells the CSS how much of the screen is really visible, once, for every screen.
 *
 * The old way measured the keyboard in each screen with React state and moved a
 * `position: fixed` bar by hand on every viewport scroll. The bar always arrived
 * a frame or two late, so it trailed your finger and jumped while you scrolled
 * with the keyboard up. Now nothing moves by hand: a game screen is laid out to
 * exactly the visible height (`--vvh`) and its answer box sits at the bottom of
 * that in ordinary layout, so it is simply wherever the bottom of the screen is.
 *
 * Writes, on `<html>`:
 *  - `--vvh`: the visible height in px (the space above the keyboard while typing);
 *  - `--kb`:  how much the keyboard covers on iOS (Android resizes the page itself,
 *             because the viewport asks it to with `interactive-widget`, so it is 0);
 *  - `data-keyboard="open"` while a text field has focus.
 *
 * Only `resize` is measured, batched to one write per frame, and nothing re-renders.
 * iOS can finish dismissing the keyboard without a final viewport event, so with
 * no text field focused the full window height is used, whatever the viewport said.
 */
export function installViewportVars(): void {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  const vv = window.visualViewport;
  let frame = 0;
  const timers: number[] = [];
  let typingBefore = false;
  let moveTimer = 0;
  /** The window's height with no keyboard up, for telling whether the browser resizes for it. */
  let restingHeight = window.innerHeight;
  /**
   * Android, asked to by `interactive-widget=resizes-content`, shrinks the page
   * itself as the keyboard rises. Then CSS alone (`100dvh`) keeps up with it in
   * the same frame, and anything measured here would only arrive a frame late.
   */
  let native = false;

  const measure = () => {
    frame = 0;
    const typing = isTextField(document.activeElement);
    if (!typing) restingHeight = window.innerHeight;
    else if (window.innerHeight < restingHeight - 120) native = true;

    // iOS slides the visible part of the page up to show the box rather than
    // resizing anything. The game screen follows that slide (`--vvtop`) instead
    // of scrolling the page back down, which was a second jump on every tap.
    const height = vv && typing ? vv.height : window.innerHeight;
    const top = vv && typing && !native ? vv.offsetTop : 0;
    const covered = vv && typing ? window.innerHeight - (vv.height + vv.offsetTop) : 0;
    root.style.setProperty('--vvh', `${Math.round(height)}px`);
    root.style.setProperty('--vvtop', `${Math.round(top)}px`);
    root.style.setProperty('--kb', `${Math.max(0, Math.round(covered))}px`);
    root.dataset.vv = native ? 'native' : 'js';

    // While the keyboard is coming or going, size changes glide at its own pace
    // rather than snapping; any other resize (turning the phone) is immediate.
    if (typing !== typingBefore) {
      typingBefore = typing;
      root.dataset.kbmove = '1';
      window.clearTimeout(moveTimer);
      moveTimer = window.setTimeout(() => delete root.dataset.kbmove, 700);
    }
    if (typing) root.dataset.keyboard = 'open';
    else delete root.dataset.keyboard;
    kbLog(`${typing ? 'typing' : 'idle'} vv ${Math.round(vv?.height ?? 0)}@${Math.round(vv?.offsetTop ?? 0)} inner ${window.innerHeight} ${native ? 'native' : 'js'}`);
  };
  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(measure);
  };
  // Measured again once the keyboard has finished moving: the events during the
  // animation report heights it is only passing through.
  const settle = () => {
    schedule();
    while (timers.length > 0) window.clearTimeout(timers.pop());
    timers.push(window.setTimeout(schedule, 120), window.setTimeout(schedule, 450));
  };

  // With a game screen up, the page underneath must not be dragged about: a drag
  // that does not start inside something that scrolls goes nowhere.
  document.addEventListener(
    'touchmove',
    (event) => {
      if (!document.querySelector('.screen.shell') || !root.dataset.keyboard) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('.screen.shell, .sheet, .sheet-backdrop, [data-scrolls]')) event.preventDefault();
    },
    { passive: false },
  );

  vv?.addEventListener('resize', schedule);
  vv?.addEventListener('scroll', schedule);
  window.addEventListener('resize', schedule);
  document.addEventListener('focusin', settle);
  document.addEventListener('focusout', settle);
  measure();
}

/** Lines for the `?kb=debug` panel; nothing is kept unless it is open. */
const KB_LOG: string[] = [];
export const KB_LOG_EVENT = 'ofc:kblog';
let kbDebug: boolean | null = null;
function kbLog(line: string): void {
  if (kbDebug === null) {
    try {
      kbDebug = new URLSearchParams(location.search).get('kb') === 'debug';
    } catch {
      kbDebug = false;
    }
  }
  if (!kbDebug) return;
  const stamp = new Date().toISOString().slice(17, 23);
  KB_LOG.unshift(`${stamp} ${line}`);
  KB_LOG.length = Math.min(KB_LOG.length, 20);
  window.dispatchEvent(new Event(KB_LOG_EVENT));
}
export function kbLogLines(): readonly string[] {
  return KB_LOG;
}

/**
 * Puts the page back where it was once the on-screen keyboard has gone.
 *
 * iOS scrolls the whole page up to show whichever field you tap, which is fine,
 * and then leaves it there when the keyboard closes, which is not: the header
 * slides off the top and a blank band sits where the keyboard used to be, until
 * you drag the page back down yourself. Browsers do not undo this for you.
 *
 * So the scroll position is noted when the first field in a run takes focus, and
 * restored once nothing is being typed into any more. Moving from one box to the
 * next keeps the keyboard up, and must not throw away where the page started.
 *
 * Game screens never scroll the page (see `installViewportVars`), so this is for
 * the rest: the landing page, the lobby's "add your own" box, the wardrobe.
 */
export function installKeyboardRestore(): void {
  if (typeof window === 'undefined') return;

  let saved: number | null = null;
  const timers: number[] = [];
  const cancel = () => {
    while (timers.length > 0) window.clearTimeout(timers.pop());
  };

  document.addEventListener('focusin', (event) => {
    if (!isTextField(event.target)) return;
    cancel();
    if (saved === null) saved = window.scrollY;
  });

  document.addEventListener('focusout', (event) => {
    if (!isTextField(event.target)) return;
    cancel();

    const restore = () => {
      // Focus went to another field, so the keyboard is staying up.
      if (isTextField(document.activeElement)) return;
      if (document.querySelector('.screen.shell')) return;
      window.scrollTo({ top: saved ?? 0, left: 0, behavior: 'instant' as ScrollBehavior });
    };

    // Once straight away, and again after the keyboard has finished closing:
    // iOS applies its own scroll during the animation and can undo the first.
    timers.push(
      window.setTimeout(restore, 60),
      window.setTimeout(() => {
        restore();
        if (!isTextField(document.activeElement)) saved = null;
      }, 420),
    );
  });
}

/**
 * Keeps the screen on while you are in a room.
 *
 * At a gathering the phone goes down on the table between turns, and after half
 * a minute it locks. A locked phone drops its connection, and when that phone was
 * the host's the whole room used to sit on the leaderboard waiting for them.
 *
 * The lock is released automatically whenever the page is hidden — switching
 * apps, or the screen being turned off by hand — so it is asked for again each
 * time the page comes back. Anything that refuses it (low-power mode, an old
 * browser, a denied permission) is ignored: the game plays exactly as before.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || sentinel || document.visibilityState !== 'visible') return;
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          void lock.release();
          return;
        }
        sentinel = lock;
        lock.addEventListener('release', () => {
          if (sentinel === lock) sentinel = null;
        });
      } catch {
        // Refused; the phone simply sleeps on its usual timer.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release();
      sentinel = null;
    };
  }, [active]);
}

/** A clock that ticks against server time, for rendering countdowns. */
export function useServerClock(serverNow: () => number, active: boolean): number {
  const [now, setNow] = useState(serverNow);

  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(serverNow()), 100);
    return () => clearInterval(timer);
  }, [serverNow, active]);

  return now;
}

/**
 * Buzzes and chimes when it becomes your turn.
 *
 * At a gathering people put the phone down between turns, so the handover has to
 * be noticeable without looking at the screen.
 */
export function useTurnAlert(isYourTurn: boolean): void {
  const wasYourTurn = useRef(isYourTurn);

  useEffect(() => {
    if (isYourTurn && !wasYourTurn.current) cue('turn');
    wasYourTurn.current = isYourTurn;
  }, [isYourTurn]);
}
